import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface AccuracyTrendChartProps {
  data: number[];
  loading?: boolean;
}

const AccuracyTrendChart: React.FC<AccuracyTrendChartProps> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="h-64 flex items-center justify-center">
        <div className="animate-pulse bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 w-full h-full rounded-xl"></div>
      </div>
    );
  }

  const chartData = useMemo(() => 
    data.map((accuracy, index) => ({
      day: `Day ${index + 1}`,
      accuracy: Math.round(accuracy * 10) / 10,
      trend: index > 0 ? accuracy - data[index - 1] : 0
    })), [data]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-slate-800/95 border border-slate-600/50 rounded-xl p-4 shadow-xl backdrop-blur-sm">
          <p className="text-slate-200 font-semibold mb-2">{label}</p>
          <p className="text-violet-400 font-bold">Accuracy: {data.accuracy}%</p>
          <p className={`font-bold ${data.trend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            Trend: {data.trend >= 0 ? '+' : ''}{data.trend.toFixed(1)}%
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full h-64 bg-gradient-to-br from-slate-50 to-white rounded-xl border border-slate-200 p-4">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="2 2" stroke="#e2e8f0" opacity={0.3} />
          <XAxis 
            dataKey="day" 
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: '#64748b' }}
          />
          <YAxis 
            domain={['dataMin - 2', 'dataMax + 2']}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: '#64748b' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line
            type="monotone"
            dataKey="accuracy"
            stroke="#6366f1"
            strokeWidth={3}
            dot={{ fill: '#6366f1', strokeWidth: 2, r: 4 }}
            animationDuration={300}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default AccuracyTrendChart;